import { useEffect, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@humansignal/ui";
import { OtherWays } from "./OtherWays";
import { AuthShell, CardThemeToggle } from "./AuthShell";
import { NetIndicator } from "./NetIndicator";
import {
  setTokens,
  apiUrl,
  clearTokens,
  clearCachedIdentity,
  getAccessToken,
  tokensJustIssued,
} from "../../utils/jwt-storage";

// WebAuthn base64url helpers (mirrors UserProfilePasskeys).
const b64uToBuf = (v: string): ArrayBuffer => {
  const norm = v.replace(/-/g, "+").replace(/_/g, "/");
  const pad = norm + "=".repeat((4 - (norm.length % 4)) % 4);
  const bin = atob(pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
};
const bufToB64u = (buf: ArrayBuffer): string => {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

import { safeNextPath, spaNavigate } from "../../utils/navigation";
import { refreshAuthHeaders } from "../../providers/ApiProvider";

/**
 * React port of users/templates/users/new-ui/user_login.html.
 *
 * Posts ``{email, password}`` to ``/api/token/obtain/`` (SimpleJWT's
 * TokenObtainPairView, mounted in ``jwt_auth/urls.py``). On success,
 * stashes the access + refresh JWTs in localStorage and redirects to
 * ``/`` — the rest of the app picks them up via the API instance's
 * Authorization header (wired in step 2b.3 of the mobile pivot).
 *
 * Web users at /user/login/ continue to use the Django HTML template;
 * this component is reached at /auth/login (route added in step 2b.2)
 * and is the only login surface the Capacitor mobile bundle ships.
 */
/** Any login surface, in either flavour, with or without query/hash.
 *  Used to reject it as a post-sign-in destination. */
const AUTO_HOME_KEY = "afri:login:auto-home";

const isLoginPath = (path: string): boolean => /^\/(auth\/login|user\/login)\/?(\?|#|$)/.test(path || "");

export const Login = () => {
  const [email, setEmail] = useState("");
  // null = probing / unknown; true = account has passkeys; false = none.
  const [hasPasskey, setHasPasskey] = useState<boolean | null>(null);
  // Passkey UI is offered when EITHER web WebAuthn or the native
  // bridge exists (email not required — discoverable credentials).
  const passkeyCapable =
    typeof window !== "undefined" &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (!!(window as any).Capacitor?.Plugins?.Passkey ||
      (!!window.PublicKeyCredential && typeof (window as any).Capacitor === "undefined"));
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(() => {
    // Sent back here by another way of signing in that refused. Whatever
    // the browser held was a previous person's: it goes before anything
    // could act as them.
    try {
      const q = new URLSearchParams(window.location.search);
      const refused = q.get("refused");
      if (!refused) return null;
      clearCachedIdentity();
      clearTokens();
      q.delete("refused");
      const search = q.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${search ? `?${search}` : ""}`);
      return refused.slice(0, 300);
    } catch {
      return null;
    }
  });
  const [statusMsg, setStatusMsg] = useState<{ text: string; positive?: boolean } | null>(null);
  // Another way in for this email, from an installed extension.
  const [elsewhere, setElsewhere] = useState<{ url: string; label: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Tanstack query client — used to seed the ``current-user`` cache
  // straight from the token-response snapshot AFTER a successful
  // sign-in so the shell's first paint reads the new identity
  // instead of the pre-login 401. Missing this call was the
  // ReferenceError the "Couldn't reach the server (ReferenceError)"
  // report surfaced: useQueryClient was imported but never invoked,
  // so ``queryClient`` in the ``try`` block resolved to undefined
  // at runtime and threw when the code hit ``.setQueryData``.
  const queryClient = useQueryClient();
  const toast = useToast();
  // Surface the ``?revoked=1`` flag from VerifyEmailRequired's poller
  // (fires when whoami returns 401 while a JWT is present — admin
  // deleted the account, disabled it, or blacklisted the token). Uses
  // the standard LS toast so it reads like every other transient
  // status the app shows, rather than an in-form banner that competes
  // with the field-level error slot. Strip the flag from the URL
  // right after reading so a reload doesn't re-surface the toast.
  // Arriving at the login screen means nobody is signed in *yet*, so any
  // stored credential belongs to a PREVIOUS session — an expired login,
  // a shared laptop, someone navigating here without signing out. Left
  // in place, the old access token and the old cached identity form a
  // self-consistent pair (the AuthProvider's binding check compares the
  // cache against the TOKEN, and both are the same stale user), so the
  // shell confidently painted the previous account — including its
  // staff-only navigation — until the new login's whoami replaced it.
  // Clearing on mount closes that window: the next render has no
  // identity to fall back to and waits for the real one.
  useEffect(() => {
    // Only STALE credentials, and never one we just minted. A blanket
    // clear here is dangerous: if anything routes back through the login
    // route immediately after a successful sign-in — which is what a
    // redirect bounce looks like — it would wipe the fresh token and
    // lock the user into an unbreakable login loop. ``tokensJustIssued``
    // exists for exactly this hazard.
    if (tokensJustIssued()) return;
    const access = getAccessToken();
    if (!access) {
      clearCachedIdentity();
      return;
    }
    let expired = false;
    try {
      let b64 = access.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      while (b64.length % 4) b64 += "=";
      const { exp } = JSON.parse(atob(b64)) as { exp?: number };
      expired = typeof exp === "number" && exp * 1000 <= Date.now();
    } catch {
      expired = true; // unreadable token is not a usable credential
    }
    if (expired) {
      clearCachedIdentity();
      clearTokens();
      return;
    }
    // Valid credential, yet here we are on the login form. That means a
    // redirect put us here rather than the user asking to sign in — the
    // reported symptom was "it showed the login page again, but going to
    // the home URL by hand showed home; it WAS logged in". Rendering a
    // login form to someone already signed in is never right, so send
    // them where they were going.
    //
    // Guarded to fire once per tab: if home bounces straight back here
    // (a server-side session that disagrees with the JWT), a second
    // attempt would ping-pong forever. On the second arrival we show
    // the form so they can sign in properly and break the cycle.
    try {
      if (window.sessionStorage.getItem(AUTO_HOME_KEY)) return;
      window.sessionStorage.setItem(AUTO_HOME_KEY, "1");
    } catch {
      return; // no sessionStorage — don't risk an unguarded loop
    }
    const target = safeNextPath(new URLSearchParams(window.location.search).get("next"));
    spaNavigate(isLoginPath(target) ? "/" : target || "/", true);
  }, []);

  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("revoked") === "1") {
        q.delete("revoked");
        const search = q.toString();
        const nextUrl = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
        window.history.replaceState({}, "", nextUrl);
        toast.show({
          message: "Your previous account is no longer available. Sign in with a different email, or sign up again.",
          type: "info",
          duration: 8000,
        });
      }
    } catch {
      /* private browsing / SSR */
    }
    // toast.show is stable across renders and we only want this on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // NB: intentionally NO "auto-redirect if already authenticated"
  // here. That creates a reload loop on web because Django's ``main``
  // view checks ``request.user.is_authenticated`` via the SESSION
  // cookie, not the JWT — a user with JWT-only auth (no session)
  // gets redirected back to /auth/login by Django, which then sees
  // the same JWT, redirects to /, which redirects back, forever.
  // The Capacitor boot script already handles the "have JWT, don't
  // show login" case on mobile; web can just show the form.
  // Two-step (email-first) UX mirroring the Django HTML template:
  //  1. ``step = "email"``  — only the email input + Continue button.
  //     Continue POSTs ``/api/auth/account-status/`` and routes based
  //     on the ``status`` response:
  //       - ``active``            → flip to ``step = "password"``
  //       - ``ask_password``      → same (private-deployment mode)
  //       - ``not_found``         → SPA nav to /auth/signup?email=…
  //       - ``pending_verify``    → show "check your inbox" status
  //       - ``pending_activation``→ show "we sent setup link" status
  //  2. ``step = "password"`` — reveal password field + Log in button
  //     which submits to /api/token/obtain/ (the JWT path this
  //     component already had).
  //
  // Keeps the form under a single <form> so mobile browser autofill
  // still associates email + password when the password field
  // appears — the browser sees an unchanged form id.
  const [step, setStep] = useState<"email" | "password">("email");

  const passkeySignIn = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Email OPTIONAL: without it the server returns a challenge
      // with no credential restriction and the authenticator lists
      // every passkey saved for this site — the account comes from
      // the credential itself (passkeys are account-bound).
      const optsRes = await fetch(apiUrl("/api/passkeys/login/options/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ email: email.trim() }),
      });
      const opts = await optsRes.json();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nativePasskey = (window as any).Capacitor?.Plugins?.Passkey;
      let payload: unknown;
      if (nativePasskey) {
        // NATIVE apps: WebAuthn can't run in the localhost WebView, so
        // the ceremony goes through the platform authenticator
        // (ASAuthorization / Credential Manager) against the real
        // relying party. The plugin takes and returns standard
        // WebAuthn JSON.
        const rpId = opts.rpId || new URL(apiUrl("/")).hostname;
        const { responseJson } = await nativePasskey.getAssertion({
          requestJson: JSON.stringify({ ...opts, rpId }),
        });
        payload = JSON.parse(responseJson);
      } else {
        const publicKey = {
          ...opts,
          challenge: b64uToBuf(opts.challenge),
          allowCredentials: (opts.allowCredentials || []).map((c: { id: string }) => ({ ...c, id: b64uToBuf(c.id) })),
        } as PublicKeyCredentialRequestOptions;
        const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
        if (!cred) return;
        const resp = cred.response as AuthenticatorAssertionResponse;
        payload = {
          id: cred.id,
          rawId: bufToB64u(cred.rawId),
          type: cred.type,
          response: {
            clientDataJSON: bufToB64u(resp.clientDataJSON),
            authenticatorData: bufToB64u(resp.authenticatorData),
            signature: bufToB64u(resp.signature),
            userHandle: resp.userHandle ? bufToB64u(resp.userHandle) : null,
          },
        };
      }
      const vr = await fetch(apiUrl("/api/passkeys/login/verify/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          credential: payload,
          challenge_token: (opts as { challenge_token?: string }).challenge_token,
        }),
      });
      const body = (await vr.json().catch(() => ({}))) as {
        access?: string;
        refresh?: string;
        redirect?: string;
        detail?: string;
      };
      if (!vr.ok || !body.access) {
        setError(body.detail || "Passkey sign-in failed. Please use your email and password instead.");
        return;
      }
      // Never let a fresh credential inherit the previous session's
      // cached identity (passkey path).
      clearCachedIdentity();
      try {
        window.sessionStorage.removeItem(AUTO_HOME_KEY);
      } catch {}
      setTokens(body.access, body.refresh ?? null);
      refreshAuthHeaders();
      // CLIENT-SIDE navigation, deliberately. The old hard
      // ``location.assign("/projects")`` raced the just-set session
      // cookie: measured in prod logs, the very next GET /projects
      // 302'd back to the login page even though verify returned 200,
      // i.e. "passkey only signs in the second time". The SPA holds
      // the JWTs already; routing in-app authenticates with them and
      // never depends on the fresh cookie. (The Django session is
      // still established server-side for later full loads.)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const h = (window as any).__afriRouterHistory;
      let dest = body.redirect || "/";
      if (/^\/projects\/?$/.test(dest) || isLoginPath(dest)) dest = "/";
      // Seed the cached identity so the shell paints the signed-in
      // menubar immediately (same as the password path).
      try {
        const meRes = await fetch(apiUrl("/api/current-user/whoami"), {
          headers: { Authorization: `Bearer ${body.access}` },
          credentials: "same-origin",
          cache: "no-store",
        });
        if (meRes.ok) {
          const me = (await meRes.json()) as {
            id?: number;
            email?: string;
            avatar?: string | null;
            first_name?: string;
            last_name?: string;
          };
          if (me && me.id != null) {
            window.localStorage.setItem("afri:last-user", JSON.stringify(me));
            if (me.avatar) {
              window.localStorage.setItem(
                "afri:last-avatar",
                JSON.stringify({
                  id: me.id,
                  email: me.email,
                  avatar: me.avatar,
                  first_name: me.first_name,
                  last_name: me.last_name,
                }),
              );
            }
          }
        }
      } catch {
        /* cosmetic only */
      }
      if (h && typeof h.replace === "function") h.replace(dest);
      else window.location.assign(dest);
    } catch (e) {
      if ((e as Error | undefined)?.name !== "NotAllowedError") {
        setError("Passkey sign-in failed. Please use your email and password instead.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onContinue = async (e: FormEvent<HTMLFormElement> | React.MouseEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setStatusMsg(null);
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      setError("Enter your email address.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(apiUrl("/api/auth/account-status/"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        status?: string;
        error?: string;
        url?: string;
        label?: string;
        only?: boolean;
      };
      if (!res.ok) {
        setError(body.error || "Could not check that email.");
        return;
      }
      const s = body.status;
      // This person signs in somewhere else (their organisation's own
      // sign-in): taken there when it is the only way, offered it beside
      // the password otherwise.
      if (s === "elsewhere" && body.url) {
        if (body.only) {
          setStatusMsg({ text: `Taking you to ${body.label || "your organization"}…`, positive: true });
          window.location.assign(body.url);
          return;
        }
        setElsewhere({ url: body.url, label: body.label || "your organization" });
        setStep("password");
        return;
      }
      if (s === "active" || s === "ask_password") {
        setStep("password");
        // Probe for passkeys (owner-requested): show the passkey
        // button when the account has one; otherwise nudge toward
        // password + creating one. Failure of the probe just means
        // no passkey UI — never blocks password login.
        // NOT inside the native app: the bundled WebView's origin is
        // http://localhost, but passkeys are bound to the cloud host's
        // relying-party ID (afriannotate.waraka.org). WebAuthn only
        // offers credentials whose RP matches the page origin, so in
        // the app the OS finds nothing and returns instantly with no
        // Face ID prompt — the button reads as broken. Hide it there;
        // it works in Safari/Chrome on the same phone. A native
        // WebAuthn bridge (ASAuthorization / Credential Manager) is
        // the real fix and is tracked as a later phase.
        const w = window as unknown as { Capacitor?: { Plugins?: { Passkey?: unknown } } };
        const nativeBridge = !!w.Capacitor?.Plugins?.Passkey;
        const inNativeApp = typeof window !== "undefined" && !!w.Capacitor && !nativeBridge;
        if (typeof window !== "undefined" && (window.PublicKeyCredential || nativeBridge) && !inNativeApp) {
          fetch(apiUrl("/api/passkeys/login/options/"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ email: trimmed }),
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((opts) => setHasPasskey(!!opts?.allowCredentials?.length))
            .catch(() => setHasPasskey(null));
        }
        // Focus the password input on next tick — DOM has just mounted.
        setTimeout(() => {
          const el = document.getElementById("password") as HTMLInputElement | null;
          el?.focus();
        }, 50);
        return;
      }
      if (s === "not_found") {
        // Carry the email forward to the signup React route so the
        // user doesn't retype it — and ``?next=`` too, so a deep link
        // (e.g. a facilitator invite QR that bounced an anonymous
        // visitor here) survives the login → signup handoff.
        const nextParam = new URLSearchParams(window.location.search).get("next");
        const nextQs = nextParam ? `&next=${encodeURIComponent(nextParam)}` : "";
        spaNavigate(`/auth/signup?email=${encodeURIComponent(trimmed)}${nextQs}`, true);
        return;
      }
      if (s === "pending_verify") {
        setStatusMsg({
          text: `Check your inbox, there's a verification link from us at ${trimmed}. Click it to finish signing up.`,
          positive: true,
        });
        return;
      }
      if (s === "pending_activation") {
        setStatusMsg({
          text: `We just sent a setup link to ${trimmed}. Click it to set your password and sign in.`,
          positive: true,
        });
        return;
      }
      setError(`Unexpected response from server (status=${s ?? "unknown"}).`);
    } catch {
      // "Network error" reads as "try again" — wrong advice when the
      // radio is off. Say what's actually happening.
      const nc = (window as any).__afri_native_connected;
      setError(
        nc === false || (typeof navigator !== "undefined" && navigator.onLine === false)
          ? "You're offline. Signing in needs an internet connection."
          : "Couldn't reach the server. Check your connection and try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // On step 1, Enter in the email field acts as Continue.
    if (step === "email") {
      await onContinue(e);
      return;
    }
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(apiUrl("/api/token/obtain/"), {
        method: "POST",
        credentials: "include", // ensure Django sessionid cookie is accepted from response
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}) as Record<string, unknown>);
        const raw =
          (body as Record<string, unknown>).detail ?? (body as Record<string, unknown>).error ?? "Sign-in failed.";
        // SimpleJWT's "No active account found with the given credentials" is
        // accurate but cold; say the neutral thing people expect.
        const detail =
          typeof raw === "string" && /no active account|given credentials/i.test(raw)
            ? "The email or password is incorrect."
            : raw;
        setError(typeof detail === "string" ? detail : "Sign-in failed.");
        return;
      }
      // Parse defensively — an empty / truncated / non-JSON 200 body
      // would otherwise throw here and get mis-attributed to a
      // network failure by the outer catch. Log the exact response
      // shape so we can diagnose a genuine bad response from the
      // token endpoint. A missing ``access`` is a server bug (200 but
      // no token) — surface it as a distinct error so the user
      // doesn't think their creds are wrong or the network is down.
      let tokenBody: {
        access?: string;
        refresh?: string;
        user?: {
          id?: number;
          email?: string;
          active_organization?: number | null;
          is_email_verified?: boolean;
          is_staff?: boolean;
          is_superuser?: boolean;
        };
      } = {};
      try {
        tokenBody = (await res.json()) as typeof tokenBody;
      } catch (parseErr) {
        console.error("[Login] token endpoint returned 200 but body was not JSON:", parseErr);
        setError("Signed in, but the server response was malformed. Please try again.");
        return;
      }
      const { access, refresh, user: userSnapshot } = tokenBody;
      if (!access) {
        console.error("[Login] token endpoint returned 200 without an access token:", tokenBody);
        setError("Sign-in succeeded on the server but the token was missing. Please try again.");
        return;
      }
      // Same for the password path.
      clearCachedIdentity();
      try {
        window.sessionStorage.removeItem(AUTO_HOME_KEY);
      } catch {}
      setTokens(access, refresh ?? null);
      refreshAuthHeaders();
      // Pre-flight the active-organization assignment BEFORE we
      // navigate to the shell. Backend ``LSTokenObtainPairSerializer``
      // already sets ``user.active_organization`` on login when the
      // user has memberships, but a few corner cases can still leave
      // it null at this point (user pre-dating the backend fix, race
      // between the save and the cached me payload, etc). Without
      // this pre-flight the shell mounts → menubar shows "No
      // organization" → the in-shell ``useEnsureActiveOrg`` hook
      // catches up ~5s later, which the user perceives as "I had to
      // pick an org manually". Doing the switch here closes that
      // window: by the time we ``spaNavigate`` to ``/``, the cloud
      // already knows our active org and the first whoami response
      // carries it through.
      const auth = { Authorization: `Bearer ${access}` } as const;
      // Always fetch the full whoami — the token-response snapshot is
      // minimal (id, email, active_org, is_email_verified, is_staff,
      // is_superuser) and lacks the avatar / initials / first_name /
      // last_name / active_organization_meta the shell renders in the
      // profile dropdown. Seeding the cache with the snapshot alone
      // left the dropdown blank for orgless users. Fetch full whoami
      // first; fall back to the snapshot only on network failure.
      let me: {
        active_organization?: number | null;
        [k: string]: unknown;
      } | null = null;
      try {
        me = await fetch(apiUrl("/api/current-user/whoami"), {
          headers: auth,
        }).then((r) => (r.ok ? r.json() : null));
        // Server-side ``LSTokenObtainPairSerializer`` runs
        // ``_repair_active_organization`` on every login, so this
        // whoami should already have ``active_organization`` set
        // (or null for a legitimately orgless user). We only fire
        // the switch handshake when active_org is null AND the user
        // has memberships — old accounts pre-repair or users just
        // added to an org via admin without the repair having run.
        if (me && !me.active_organization) {
          const orgsRes = await fetch(apiUrl("/api/organizations/"), {
            headers: auth,
          }).then((r) => (r.ok ? r.json() : null));
          const orgList = Array.isArray(orgsRes)
            ? orgsRes
            : ((orgsRes as { results?: Array<{ id: number }> } | null)?.results ?? []);
          const first = orgList[0];
          if (first?.id) {
            await fetch(apiUrl("/api/organizations/switch"), {
              method: "POST",
              headers: { "Content-Type": "application/json", ...auth },
              body: JSON.stringify({ organization_id: first.id }),
            });
            const after = await fetch(apiUrl("/api/current-user/whoami"), {
              headers: auth,
            }).then((r) => (r.ok ? r.json() : null));
            if (after) me = after;
          }
        }
      } catch {
        /* network blip — fall back to the token-response snapshot so
           the seed at least has an email + id. The in-shell
           ``useEnsureActiveOrg`` hook will refresh whoami once the
           menubar mounts. */
      }
      if (!me && userSnapshot) me = { ...userSnapshot };
      // Seed the tanstack query cache directly from the fresh whoami
      // we just fetched. Without this, the menubar's first paint
      // reads from the *old* cached ``current-user`` value (the one
      // produced by the pre-login 401), which on iOS WKWebView can
      // stick visibly long enough for the user to see "No
      // organization" even though the cloud has the right value
      // ready. ``invalidateQueries`` below still kicks off a
      // background refetch to reconcile, but the UI is already
      // correct from this seed.
      if (me) {
        queryClient.setQueryData(["current-user"], me);
      }
      // Drop any cached 401 results from queries that ran during the
      // unauthenticated boot (currentUserAtom in particular). Without
      // this the app shell stays in the "user is null" branch and the
      // dropdown / sidebar / project list render their logged-out
      // skeleton even though we now have a valid JWT.
      await queryClient.invalidateQueries();
      // Warm the identity visuals BEFORE the shell paints: the avatar
      // and first project logos are signed GCS URLs generated cold on
      // first login ("several seconds before the avatar appears").
      // Fire-and-forget image fetches put them in the HTTP/SW cache
      // so the home renders complete.
      void (async () => {
        try {
          const r = await fetch(apiUrl("/api/projects/?include=id,logo&page_size=6"), {
            headers: { Authorization: `Bearer ${access}` },
            cache: "no-store",
          });
          const body = await r.json().catch(() => null);
          const logos = (Array.isArray(body) ? body : (body?.results ?? []))
            .map((p: { logo?: string | null }) => p.logo)
            .filter(Boolean) as string[];
          const avatar = (me as { avatar?: string | null } | null)?.avatar;
          for (const u of [avatar, ...logos].filter(Boolean).slice(0, 8)) {
            const img = new Image();
            img.src = u as string;
          }
        } catch (_) {
          /* cosmetic warm-up only */
        }
      })();
      // Honour ?next= when the API 401 handler bounced us here from a
      // protected page; otherwise default to the home redirect.
      const next = new URLSearchParams(window.location.search).get("next");
      // safeNextPath: same-origin relative paths only — spaNavigate is a
      // hard location call on web, so an unchecked next= is an open redirect.
      let dest = safeNextPath(next);
      // Orgless users have no org projects, so the default ``/projects``
      // landing (Django's post-login target when the homepage flag is off)
      // is a dead end for them. Send them to their home instead, which
      // renders the no-org / Discover-oriented landing. Deep links to a
      // specific joined project (``/projects/123``) are still honoured.
      // Sign-in lands on Home for everyone; only a real deep link
      // (``/projects/123``, a settings page, …) is honoured. A bare
      // ``/projects`` next= is what the anonymous bounce leaves behind,
      // not a destination the user chose.
      // Never land back on a login screen. The old check listed only the
      // Django route ("/user/login"), missing the SPA's own "/auth/login"
      // — and a sign-out redirect chain (/ -> /user/login -> /auth/login)
      // readily leaves ``next=/auth/login`` behind. A successful sign-in
      // then navigated straight back to the login page, over and over,
      // which reads exactly like "it won't log me in".
      if (/^\/projects\/?$/.test(dest) || isLoginPath(dest)) dest = "/";
      // Seed the cached identity the shell reads on boot (AuthProvider's
      // last-user + the menubar's last-avatar) so the avatar and the
      // signed-in menubar paint on the first frame after navigation —
      // no "Sign in" flash while whoami is re-fetched.
      try {
        if (me && (me as { id?: number }).id != null) {
          window.localStorage.setItem("afri:last-user", JSON.stringify(me));
          const m = me as {
            id: number;
            email?: string;
            avatar?: string | null;
            first_name?: string;
            last_name?: string;
          };
          if (m.avatar) {
            window.localStorage.setItem(
              "afri:last-avatar",
              JSON.stringify({
                id: m.id,
                email: m.email,
                avatar: m.avatar,
                first_name: m.first_name,
                last_name: m.last_name,
              }),
            );
          }
        }
      } catch {
        /* private mode / quota — cosmetic only */
      }
      spaNavigate(dest, true);
    } catch (err) {
      // Log the actual error so a user reporting "couldn't reach the
      // server" can share a devtools console screenshot that tells us
      // WHY (TypeError=network vs SyntaxError=bad JSON body vs
      // AbortError=timeout, etc). The user-facing copy stays generic
      // — this line is diagnostic only.
      console.error("[Login] sign-in POST threw:", err);
      // Cloud is unreachable. Allow the SAME local user to sign
      // back in offline — their cached identity, projects, and
      // any queued annotations are sitting in IDB and still tied
      // to the JWT in localStorage (offline sign-out preserved
      // it). A DIFFERENT user can't slip through this branch:
      // we compare the entered email against the cached whoami
      // email and only proceed when they match.
      // Offline sign-in is a MOBILE (Capacitor) feature — it reads the
      // cached identity out of IndexedDB. Keep it OUT of the plain-web
      // path so web never depends on IndexedDB (private mode, etc.):
      // gate the whole fallback behind Capacitor + an IDB-availability
      // check. On web we fall straight through to the generic error.
      const onCapacitor = typeof (window as unknown as { Capacitor?: unknown }).Capacitor !== "undefined";
      if (onCapacitor && typeof indexedDB !== "undefined") {
        try {
          const dbReq = indexedDB.open("afri-offline");
          const cached = await new Promise<{ email?: string } | null>((resolve) => {
            dbReq.onerror = () => resolve(null);
            dbReq.onsuccess = () => {
              const db = dbReq.result;
              if (!db.objectStoreNames.contains("api_cache")) {
                db.close();
                resolve(null);
                return;
              }
              const tx = db.transaction("api_cache", "readonly");
              const g = tx.objectStore("api_cache").get("/api/current-user/whoami");
              g.onsuccess = () => {
                db.close();
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                resolve((g.result?.body as any) ?? null);
              };
              g.onerror = () => {
                db.close();
                resolve(null);
              };
            };
          });
          const cachedEmail = cached?.email?.trim().toLowerCase() ?? "";
          const enteredEmail = email.trim().toLowerCase();
          // ``hasTokens()`` confirms the JWT is still in
          // localStorage — required because ``offline sign-out``
          // keeps it but ``online sign-out`` clears it. If JWT is
          // absent we can't actually authenticate API calls offline.
          const hasJwt = !!window.localStorage.getItem("afri:jwt:access");
          if (cachedEmail && enteredEmail === cachedEmail && hasJwt) {
            // Match — restore the session. Skip the queryClient
            // pre-seed (no fresh whoami fetched here); the post-nav
            // route will fetch via offline-fetch's IDB replay.
            spaNavigate("/", true);
            return;
          }
          if (cachedEmail && enteredEmail !== cachedEmail) {
            setError("You can't sign in as a different user while offline. Reconnect first.");
            return;
          }
        } catch (_) {
          /* fall through to generic error */
        }
      }
      // Include the error type name in the user-facing copy so a
      // screenshot alone tells us whether this was a network problem
      // (TypeError), a timeout (AbortError), or something else. If we
      // can't get any name, fall through to the generic copy.
      const errName = err && (err as Error).name ? ` (${(err as Error).name})` : "";
      setError(`Couldn't reach the server${errName}. Check your connection and try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <div className="form-wrapper">
        <div className="form-wrapper-header">
          <h2>Log in</h2>
          <CardThemeToggle />
          <NetIndicator />
        </div>
        <form id="login-form" onSubmit={onSubmit}>
          <div className="input-wrapper">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              inputMode="email"
              className="lsf-input-ls"
              name="email"
              id="email"
              autoComplete="email"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              readOnly={step === "password"}
            />
          </div>
          {step === "password" && (
            <div className="input-wrapper">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                className="lsf-input-ls"
                name="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
            </div>
          )}
          {error && <p className="error">{error}</p>}
          {statusMsg && (
            <p
              style={{
                color: statusMsg.positive
                  ? "var(--color-positive-content, #047857)"
                  : "var(--color-error-content, #b91c1c)",
                margin: "8px 0",
                fontSize: "var(--font-size-body-small)",
                lineHeight: 1.4,
              }}
            >
              {statusMsg.text}
            </p>
          )}
          {step === "email" ? (
            /* Continue (~80%) + icon-only passkey (~20%) — the same
               compact pair as the password step; no full-width
               passkey button (owner request). */
            <div className="login-actions">
              <button type="submit" aria-label="Continue" className="login-button" disabled={submitting}>
                {submitting ? "Checking…" : "Continue"}
              </button>
              {passkeyCapable && (
                <button
                  type="button"
                  onClick={passkeySignIn}
                  disabled={submitting}
                  className="login-button login-passkey"
                  aria-label="Sign in with a passkey"
                  title="Sign in with a passkey"
                >
                  {/* Fingerprint, not a key — reads as "biometric
                      sign-in" at a glance (owner request). */}
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 11a4 4 0 0 0-4 4c0 1.6-.3 3.1-.9 4.4" />
                    <path d="M12 11a4 4 0 0 1 4 4c0 2-.2 3.5-.5 4.8" />
                    <path d="M12 15v2.5c0 1.2-.1 2.3-.4 3.3" />
                    <path d="M5.2 17.9A8 8 0 0 1 4 15a8 8 0 0 1 12.9-6.3" />
                    <path d="M19.4 11.5c.4 1.1.6 2.3.6 3.5 0 1-.1 2-.2 2.9" />
                    <path d="M7.8 6.5A8 8 0 0 1 12 5.2c1.5 0 2.9.4 4.1 1.1" />
                  </svg>
                </button>
              )}
            </div>
          ) : (
            /* Log in (~80%) + icon-only passkey (~20%) on one row — same
               design as the Django template login (user_login.html). The
               passkey label lives on ``title`` hover text + aria-label. */
            <div className="login-actions">
              <button type="submit" aria-label="Log In" className="login-button" disabled={submitting}>
                {submitting ? "Signing in…" : "Log in"}
              </button>
              {/* passkey lives on the EMAIL step only — it never
                  needed the password, and two icons read as clutter. */}
            </div>
          )}
          {step === "password" && hasPasskey === false && (
            <p
              style={{
                margin: "10px 0 0",
                fontSize: "var(--font-size-body-small)",
                color: "var(--color-neutral-content-subtler, #64748b)",
                lineHeight: 1.4,
              }}
            >
              No passkey on this account yet — log in with your password, then add one under Profile → Security &amp;
              access.
            </p>
          )}
          {step === "password" && (
            <button
              type="button"
              onClick={() => {
                setStep("email");
                setPassword("");
                setError(null);
                setHasPasskey(null);
              }}
              style={{
                marginTop: 8,
                background: "none",
                border: "none",
                color: "var(--color-primary-content)",
                fontSize: "var(--font-size-body-small)",
                cursor: "pointer",
                padding: 0,
              }}
              aria-label="Use a different email"
            >
              ← Use a different email
            </button>
          )}
        </form>
        <OtherWays also={step === "password" ? elsewhere : null} />
        <div style={{ marginTop: 12, textAlign: "center" }}>
          {/* SPA nav to the React /auth/forgot-password route (built
              during the auth consolidation pivot). A plain
              ``<a href="/forgot-password/">`` used to trigger a full
              navigation to Django's URL, on Capacitor's local
              origin that path has nothing to serve, so the WebView
              dropped to a blank Home render. Using ``spaNavigate``
              keeps the WebView on the bundle and the React router
              picks up ``/auth/forgot-password`` cleanly. */}
          <button
            type="button"
            onClick={() => spaNavigate("/auth/forgot-password", true)}
            style={{
              fontSize: "var(--font-size-body-small)",
              background: "none",
              border: "none",
              padding: 0,
              color: "var(--color-primary-content, #1e40af)",
              textDecoration: "underline",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            Forgot password?
          </button>
        </div>
      </div>
      <div className="text-wrapper">
        <p>Don't have an account?</p>
        <a
          href="/auth/signup"
          onClick={(e) => {
            // Always SPA-nav; a plain full navigation to /auth/signup
            // works on the cloud (Django serves the SPA index there
            // too) but on Capacitor's ``http://localhost`` origin the
            // WebView has only the bundled index.html — a full nav
            // goes to a real ``/auth/signup`` URL that only exists
            // via React Router in the ALREADY-mounted SPA. Prevent
            // the default and use spaNavigate so the route change
            // stays inside the loaded bundle.
            e.preventDefault();
            // Capacitor: signup needs the cloud (creates a real
            // account on the server). When the cloud is offline,
            // opening the signup page is pointless — every submit
            // would just throw. Surface a clear message and stay on
            // the login form. Defaults true on first launch before
            // any probe has run.
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const onCapacitor = typeof (window as any).Capacitor !== "undefined";
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const cloudUp = (window as any).__afri_cloud_up !== false;
              if (onCapacitor && !cloudUp) {
                setError("You have to be online to create an account.");
                return;
              }
            } catch (_) {
              /* fall through to nav */
            }
            spaNavigate("/auth/signup", true);
          }}
        >
          Sign up
        </a>
      </div>
    </AuthShell>
  );
};

export default Login;
